/* ==========================================================================
   YumCars Dilution Calculator — widget script
   Self-mounts into every element with the [data-ycdc-calculator] attribute.
   Include yumcars-dilution-widget.css alongside this file, then drop:

       <div data-ycdc-calculator></div>

   anywhere on the page (see embed-snippet.html for a full example, including
   how to reference both files as Shopify theme assets).

   Safe to include on every page: it does nothing until it finds a mount
   point, and multiple mount points on the same page each get their own
   independent instance (no shared/global state, no id collisions).
   ========================================================================== */
(function(){
  "use strict";

  // ---------------------------------------------------------------
  // PRODUCT DATA
  // mode: "ratio"  -> concentrate:water ratio, user enters total mix volume
  // mode: "dose"   -> fixed ml dose of concentrate, topped up with water to
  //                   fill the user's own container/bucket/reservoir size
  // mode: "neat"   -> not to be diluted / used as-is
  //
  // Every "ratio" product gets a trailing {custom:true} use case that reveals
  // a manual ratio input, and every "dose" product gets a trailing
  // {custom:true} use case that reveals a manual dose input — so presets
  // stay one click away, but nothing is locked to the presets.
  // ---------------------------------------------------------------
  var CUSTOM_RATIO_UC = {label:"Enter a custom ratio…", custom:true};
  var CUSTOM_DOSE_UC = {label:"Enter a custom dose…", custom:true};

  var PRODUCTS = [
    {
      id:"rinse-less",
      name:"Yum Rinse-Less",
      mode:"ratio",
      note:"Shake well before use.",
      useCases:[
        {label:"Full waterless wash", ratio:149},
        {label:"Traditional rinse-less bucket wash", ratio:256},
        {label:"Quick wash — well-maintained car", ratio:349},
        {label:"Interior / glass wipe-down", ratio:399},
        CUSTOM_RATIO_UC
      ]
    },
    {
      id:"citrus",
      name:"Yum Citrus",
      mode:"ratio",
      note:"21% raw concentrate — spray and allow to dwell before agitating or foaming.",
      useCases:[
        {label:"Heavy contamination (neat-strength)", ratio:1},
        {label:"General maintenance (stronger)", ratio:5},
        {label:"General maintenance (lighter)", ratio:10},
        {label:"Pre-wash \"bite\" — well-maintained car", ratio:20},
        CUSTOM_RATIO_UC
      ]
    },
    {
      id:"panel-wipe",
      name:"Yum Panel Wipe",
      mode:"ratio",
      note:"Used neat for heavy paint decontamination; dilute only for general-purpose panel prep.",
      useCases:[
        {label:"General purpose panel prep spray", ratio:1},
        CUSTOM_RATIO_UC
      ]
    },
    {
      id:"dress",
      name:"Yum Dress",
      mode:"ratio",
      note:"For tyres and exterior trim, use neat with the HexGrip applicator — do not dilute for that use.",
      useCases:[
        {label:"Engine bay application", ratio:5},
        CUSTOM_RATIO_UC
      ]
    },
    {
      id:"deodoriser",
      name:"Yum Deodoriser",
      mode:"ratio",
      note:"Best scent strength and longevity is neat. Dilute only if you prefer a lighter scent — do not spray diluted or neat directly on plastic trims (can spot).",
      useCases:[
        {label:"Lighter scent (max recommended dilution)", ratio:1},
        CUSTOM_RATIO_UC
      ]
    },
    {
      id:"wash",
      name:"Yum Wash",
      mode:"dose",
      note:"Add the dose to your empty bucket or foam cannon reservoir first, then top up with water to your container's fill line.",
      useCases:[
        {label:"Bucket wash", dose:100},
        {label:"Foam cannon / lance (low end)", dose:100},
        {label:"Foam cannon / lance (high end)", dose:150},
        CUSTOM_DOSE_UC
      ]
    },
    {
      id:"foam",
      name:"Yum Foam",
      mode:"dose",
      note:"Dose is calibrated for an MTM PF22.2 foam cannon reservoir — adjust to taste for other cannon models. Dwell 5–10 min, rinse top-down.",
      useCases:[
        {label:"Foam cannon (low end)", dose:100},
        {label:"Foam cannon (high end)", dose:150},
        CUSTOM_DOSE_UC
      ]
    },
    {
      id:"reset",
      name:"Yum Reset",
      mode:"dose",
      note:"Dose by target pH strength — add to your bucket or sprayer, then top up with water to your container size. Exact base volume isn't specified by the brand; if precision matters for a job, confirm with VCM Performance before relying on this.",
      useCases:[
        {label:"Target pH ~6.0 (mildest)", dose:50},
        {label:"Target pH ~5.5 (medium)", dose:100},
        {label:"Target pH ~4 (strongest)", dose:150},
        CUSTOM_DOSE_UC
      ]
    },
    {
      id:"custom",
      name:"Custom ratio (any product)",
      mode:"custom",
      note:"Enter any 1:X ratio manually — handy for products not listed above, or for trying a different strength."
    },
    {
      id:"undress",
      name:"Yum Undress",
      mode:"neat",
      note:"Formulated to be used neat, straight from the bottle. Diluting causes a drastic drop in performance. Use with a tyre brush for agitation."
    },
    {
      id:"wheels",
      name:"Yum Wheels",
      mode:"neat",
      note:"Formulated to be used neat — do not dilute. Rinse wheels before and after use; keep wheels cool and out of direct sun."
    },
    {
      id:"glass",
      name:"Yum Glass",
      mode:"neat",
      note:"Ready to use as-is. Spray and wipe with the two-towel method."
    },
    {
      id:"detailer",
      name:"Yum Detailer",
      mode:"neat",
      note:"Ready-to-use drying aid / detail spray — no dilution required."
    },
    {
      id:"interior",
      name:"Yum Interior",
      mode:"neat",
      note:"Ready to use as standard. For sensitive or dyed leather only, some owners test a weaker mix (up to 50% water) — always spot-test first."
    }
  ];

  var VOLUME_PRESETS = [
    {label:"500ml bottle", value:500},
    {label:"1L bottle", value:1000},
    {label:"5L", value:5000},
    {label:"10L bucket", value:10000},
    {label:"20L bucket", value:20000}
  ];

  var DEFAULTS = { customWaterParts: 10, customDose: "" };

  var WIDGET_MARKUP =
    '<div class="ycdc-calc">' +
      '<div class="ycdc-header">' +
        '<div class="ycdc-header-top">' +
          '<div>' +
            '<h2 class="ycdc-title">YumCars Dilution Calculator</h2>' +
            '<p class="ycdc-subtitle">Get the right mix, every time.</p>' +
          '</div>' +
          '<button type="button" class="ycdc-reset-btn" data-role="reset-btn">Reset</button>' +
        '</div>' +
      '</div>' +
      '<div class="ycdc-body">' +
        '<div class="ycdc-field">' +
          '<label class="ycdc-label">Product</label>' +
          '<div class="ycdc-select-wrap"><select data-role="product"></select></div>' +
        '</div>' +
        '<div class="ycdc-field" data-role="usecase-field" style="display:none;">' +
          '<label class="ycdc-label">Use case</label>' +
          '<div class="ycdc-select-wrap"><select data-role="usecase"></select></div>' +
        '</div>' +
        '<div class="ycdc-field" data-role="custom-ratio-field" style="display:none;">' +
          '<label class="ycdc-label">Ratio (1 part product : parts water)</label>' +
          '<div class="ycdc-row2">' +
            '<input type="number" data-role="custom-part" value="1" min="0" step="0.1" disabled>' +
            '<input type="number" data-role="custom-water-parts" value="10" min="0.1" step="0.1">' +
          '</div>' +
        '</div>' +
        '<div class="ycdc-field" data-role="custom-dose-field" style="display:none;">' +
          '<label class="ycdc-label">Dose (mL of concentrate)</label>' +
          '<input type="number" data-role="custom-dose" min="0" step="1" placeholder="e.g. 80">' +
        '</div>' +
        '<div class="ycdc-field" data-role="volume-field" style="display:none;">' +
          '<label class="ycdc-label" data-role="volume-label">Total mix volume</label>' +
          '<input type="number" data-role="volume" min="0" step="1" placeholder="e.g. 500">' +
          '<div class="ycdc-presets" data-role="volume-presets"></div>' +
        '</div>' +
        '<div data-role="output"></div>' +
      '</div>' +
      '<div class="ycdc-footer">Ratios sourced from published VCM Performance / YumCars product guidance as of Aug 2026. Always check the current product label before mixing — formulations and recommended ratios can change. This tool provides guidance only and is not a substitute for label instructions.</div>' +
    '</div>';

  function fmt(n){
    if (!isFinite(n)) return "—";
    if (Math.abs(n) >= 1000) return (n/1000).toFixed(2).replace(/\.00$/,"") + " L";
    return (Math.round(n*10)/10) + " mL";
  }

  function trimNum(n){
    return (Math.round(n*100)/100).toString();
  }

  function findProduct(id){
    for (var i=0;i<PRODUCTS.length;i++){ if (PRODUCTS[i].id === id) return PRODUCTS[i]; }
    return null;
  }

  function mount(root){
    if (root.getAttribute("data-ycdc-mounted") === "true") return; // avoid double-init
    root.setAttribute("data-ycdc-mounted", "true");
    root.classList.add("ycdc-widget");
    root.innerHTML = WIDGET_MARKUP;

    var q = function(role){ return root.querySelector('[data-role="' + role + '"]'); };

    var productSel = q("product");
    var usecaseField = q("usecase-field");
    var usecaseSel = q("usecase");
    var customRatioField = q("custom-ratio-field");
    var customWaterParts = q("custom-water-parts");
    var customDoseField = q("custom-dose-field");
    var customDoseInput = q("custom-dose");
    var volumeField = q("volume-field");
    var volumeLabel = q("volume-label");
    var volumeInput = q("volume");
    var volumePresetsWrap = q("volume-presets");
    var output = q("output");
    var resetBtn = q("reset-btn");

    // Build product dropdown, grouped
    var groups = [
      {label:"Ratio-based concentrates (dilute to a set ratio)", modes:["ratio"]},
      {label:"Dose-based concentrates (fixed dose, top up your container)", modes:["dose"]},
      {label:"Custom", modes:["custom"]},
      {label:"Use neat — do not dilute", modes:["neat"]}
    ];
    groups.forEach(function(g){
      var og = document.createElement("optgroup");
      og.label = g.label;
      PRODUCTS.filter(function(p){ return g.modes.indexOf(p.mode) !== -1; }).forEach(function(p){
        var opt = document.createElement("option");
        opt.value = p.id;
        opt.textContent = p.name;
        og.appendChild(opt);
      });
      if (og.children.length) productSel.appendChild(og);
    });

    function buildVolumePresets(){
      volumePresetsWrap.innerHTML = "";
      VOLUME_PRESETS.forEach(function(vp){
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ycdc-preset-btn";
        btn.textContent = vp.label;
        btn.addEventListener("click", function(){
          volumeInput.value = vp.value;
          Array.prototype.forEach.call(volumePresetsWrap.children, function(c){ c.classList.remove("active"); });
          btn.classList.add("active");
          updateOutput();
        });
        volumePresetsWrap.appendChild(btn);
      });
    }
    buildVolumePresets();

    function currentProduct(){ return findProduct(productSel.value); }

    function currentUseCase(p){
      if (!p || !p.useCases) return null;
      var idx = parseInt(usecaseSel.value, 10);
      return p.useCases[idx] || p.useCases[0];
    }

    function clearVolumePresetHighlight(){
      Array.prototype.forEach.call(volumePresetsWrap.children, function(c){ c.classList.remove("active"); });
    }

    // Rebuilds the use-case dropdown and shows/hides the manual-entry fields.
    // Only called when the PRODUCT changes (or on reset) — never on every
    // keystroke — so a use-case selection is never silently reset underneath
    // the user while they're typing a volume or a manual ratio/dose.
    function onProductChanged(){
      var p = currentProduct();
      if (!p) return;

      if (p.useCases && p.useCases.length){
        usecaseField.style.display = "";
        usecaseSel.innerHTML = "";
        p.useCases.forEach(function(uc, i){
          var opt = document.createElement("option");
          opt.value = i;
          var suffix = uc.custom ? "" :
            (p.mode === "ratio" ? " (1:" + uc.ratio + ")" : " (" + uc.dose + " mL dose)");
          opt.textContent = uc.label + suffix;
          usecaseSel.appendChild(opt);
        });
      } else {
        usecaseField.style.display = "none";
      }

      if (p.mode === "ratio" || p.mode === "custom"){
        volumeField.style.display = "";
        volumeLabel.textContent = "Total mix volume";
      } else if (p.mode === "dose"){
        volumeField.style.display = "";
        volumeLabel.textContent = "Your container / bucket size";
      } else {
        volumeField.style.display = "none";
      }

      clearVolumePresetHighlight();
      volumeInput.value = "";
      customWaterParts.value = DEFAULTS.customWaterParts;
      customDoseInput.value = DEFAULTS.customDose;

      onUseCaseChanged();
    }

    // Shows/hides the manual ratio or dose fields based on the current
    // use-case selection, then recomputes the output. Called whenever the
    // product OR the use-case selection changes.
    function onUseCaseChanged(){
      var p = currentProduct();
      var uc = currentUseCase(p);

      customRatioField.style.display =
        (p.mode === "custom" || (p.mode === "ratio" && uc && uc.custom)) ? "" : "none";
      customDoseField.style.display =
        (p.mode === "dose" && uc && uc.custom) ? "" : "none";

      updateOutput();
    }

    // Pure recompute-and-render — safe to call on every keystroke. Never
    // touches which options exist in a dropdown.
    function updateOutput(){
      var p = currentProduct();
      if (!p){ output.innerHTML = ""; return; }
      computeAndShowOutput(p);
    }

    function computeAndShowOutput(p){
      output.innerHTML = "";

      if (p.mode === "neat"){
        var div = document.createElement("div");
        div.className = "ycdc-result neat";
        div.innerHTML = '<div class="ycdc-warn-text"><strong>Do not dilute.</strong> ' + p.note + '</div>';
        output.appendChild(div);
        return;
      }

      var vol = parseFloat(volumeInput.value);
      var hasVol = !isNaN(vol) && vol > 0;

      if (p.mode === "ratio"){
        var uc = currentUseCase(p);
        var isCustom = uc && uc.custom;
        var ratio = isCustom ? parseFloat(customWaterParts.value) : (uc ? uc.ratio : null);
        var body = "";
        if (hasVol && ratio > 0){
          var totalParts = 1 + ratio;
          var product = vol / totalParts;
          var water = vol - product;
          body = resultGridHTML(product, water, "1 : " + trimNum(ratio));
        } else if (!hasVol && (!isCustom || ratio > 0)) {
          body = '<div class="ycdc-note">Enter a total mix volume above to see amounts.</div>';
        } else {
          body = '<div class="ycdc-note">Enter a ratio and a total mix volume above to see amounts.</div>';
        }
        wrapResult(body, p.note);
      }

      else if (p.mode === "custom"){
        var waterParts = parseFloat(customWaterParts.value);
        var body2 = "";
        if (hasVol && waterParts > 0){
          var totalParts2 = 1 + waterParts;
          var product2 = vol / totalParts2;
          var water2 = vol - product2;
          body2 = resultGridHTML(product2, water2, "1 : " + trimNum(waterParts));
        } else {
          body2 = '<div class="ycdc-note">Enter a ratio and total mix volume above to see amounts.</div>';
        }
        wrapResult(body2, p.note);
      }

      else if (p.mode === "dose"){
        var uc2 = currentUseCase(p);
        var isCustomDose = uc2 && uc2.custom;
        var dose = isCustomDose ? parseFloat(customDoseInput.value) : (uc2 ? uc2.dose : null);
        var body3 = "";
        if (hasVol && dose > 0){
          if (dose > vol){
            body3 = '<div class="ycdc-warn-text">The dose (' + fmt(dose) + ') is larger than your container (' + fmt(vol) + '). Use a bigger container or reduce the dose proportionally.</div>';
            var div3 = document.createElement("div");
            div3.className = "ycdc-result warn";
            div3.innerHTML = body3;
            output.appendChild(div3);
            return;
          }
          var water3 = vol - dose;
          body3 = resultGridHTML(dose, water3, null);
        } else if (!hasVol) {
          body3 = '<div class="ycdc-note">Enter your container/bucket size above to see amounts.</div>';
        } else {
          body3 = '<div class="ycdc-note">Enter a dose (mL) above to see amounts.</div>';
        }
        wrapResult(body3, p.note);
      }
    }

    function resultGridHTML(product, water, ratioLabel){
      var pill = ratioLabel ? '<div class="ycdc-ratio-pill">Ratio ' + ratioLabel + '</div>' : "";
      return pill +
        '<div class="ycdc-result-grid">' +
          '<div class="ycdc-result-item"><div class="val">' + fmt(product) + '</div><div class="lbl">Concentrate</div></div>' +
          '<div class="ycdc-result-item"><div class="val">' + fmt(water) + '</div><div class="lbl">Water</div></div>' +
        '</div>';
    }

    function wrapResult(bodyHTML, note){
      var div = document.createElement("div");
      div.className = "ycdc-result";
      div.innerHTML = bodyHTML + (note ? '<div class="ycdc-note">' + note + '</div>' : "");
      output.appendChild(div);
    }

    productSel.addEventListener("change", onProductChanged);
    usecaseSel.addEventListener("change", onUseCaseChanged);
    customWaterParts.addEventListener("input", updateOutput);
    customDoseInput.addEventListener("input", updateOutput);
    volumeInput.addEventListener("input", function(){
      clearVolumePresetHighlight();
      updateOutput();
    });
    resetBtn.addEventListener("click", function(){
      productSel.selectedIndex = 0;
      onProductChanged();
    });

    onProductChanged();
  }

  function init(){
    var mounts = document.querySelectorAll("[data-ycdc-calculator]");
    Array.prototype.forEach.call(mounts, mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Exposed so the widget can be re-initialised manually after AJAX-loaded
  // content (e.g. Shopify quick-view / infinite-scroll sections).
  window.YumCarsDilutionCalculator = { init: init };
})();
